import { defineStore } from "pinia";
import { ref, computed } from "vue";

// Catálogo estático — espelho de FuncionalidadeSyncService.cs.
// Usado como fallback quando a API /planos/menu-acessos está indisponível
// (pico de energia, rede instável, timeout). Garante que o menu NUNCA fique branco.
const CATALOGO_ESTATICO = {
  dashboard: {
    label: "Dashboard",
    icone: "Home",
    rota: "/inicio",
    grupo: "principal",
    ordem: 1,
  },
  agendamento: {
    label: "Agendamento",
    icone: "Calendar",
    rota: "/agenda",
    grupo: "principal",
    ordem: 2,
  },
  confirmacao_agendamento: {
    label: "Confirmações",
    icone: "CheckCircle",
    rota: "/confirmacao-agendamento",
    grupo: "principal",
    ordem: 3,
  },
  acompanhamento_atendimento: {
    label: "Acompanhamento",
    icone: "Activity",
    rota: "/acompanhamento-atendimento",
    grupo: "principal",
    ordem: 4,
  },
  pdv: {
    label: "PDV",
    icone: "ShoppingCart",
    rota: "/pdv",
    grupo: "principal",
    ordem: 5,
  },
  historico: {
    label: "Histórico",
    icone: "ClipboardList",
    rota: "/historico",
    grupo: "clientes",
    ordem: 1,
  },
  avaliacoes: {
    label: "Avaliações",
    icone: "Star",
    rota: "/avaliacoes",
    grupo: "clientes",
    ordem: 2,
  },
  fidelidade: {
    label: "Fidelidade",
    icone: "Gift",
    rota: "/fidelidade",
    grupo: "clientes",
    ordem: 3,
  },
  notificacoes_personalizadas: {
    label: "Notificações",
    icone: "Send",
    rota: "/notificacoes",
    grupo: "clientes",
    ordem: 4,
  },
  funcionarios: {
    label: "Funcionários",
    icone: "Users",
    rota: "/funcionarios",
    grupo: "gestao",
    ordem: 1,
  },
  relatorios: {
    label: "Relatórios",
    icone: "BarChart2",
    rota: "/relatorios",
    grupo: "gestao",
    ordem: 2,
  },
  relatorios_avancados: {
    label: "Relatórios Avançados",
    icone: "BarChart2",
    rota: "/relatorios-avancados",
    grupo: "gestao",
    ordem: 2,
  },
  estoque: {
    label: "Estoque",
    icone: "Package",
    rota: "/estoque",
    grupo: "gestao",
    ordem: 3,
  },
  combos: {
    label: "Combos de Serviços",
    icone: "Layers",
    rota: "/combos",
    grupo: "gestao",
    ordem: 4,
  },
  cupons: {
    label: "Cupons de Desconto",
    icone: "Ticket",
    rota: "/cupons",
    grupo: "gestao",
    ordem: 5,
  },
  servicos: {
    label: "Serviços",
    icone: "Scissors",
    rota: "/servicos",
    grupo: "gestao",
    ordem: 6,
  },
  promocoes: {
    label: "Promoções",
    icone: "Tag",
    rota: "/promocoes",
    grupo: "gestao",
    ordem: 7,
  },
};

export const useGlobalStore = defineStore("global", () => {
  const empresaLogada = ref({});
  const cnpjLogado = ref("");
  const tokenExpiracaoEm = ref(null);
  const parametro = ref({});

  // Restaura menus do sessionStorage apenas como fallback para reloads (F5).
  // ATENÇÃO: Chrome pode restaurar sessionStorage ao reabrir abas (session restore).
  // Por isso usamos _menusCarregadosDaApi (flag em memória) para garantir que a API
  // seja chamada sempre na primeira navegação após abrir o browser.
  const _menusCache = (() => {
    try {
      const s = sessionStorage.getItem("menus_cache");
      return s ? JSON.parse(s) : [];
    } catch {
      return [];
    }
  })();

  // Flag em memória (não persistida): true somente após receber resposta da API
  // nesta sessão JavaScript. Zera a cada abertura do browser/tab.
  let _menusCarregadosDaApi = false;

  // Itens do menu vindos do banco: [{ chave, label, icone, rota, grupo, ordem }]
  const menus = ref(_menusCache);
  // Chaves do plano contratado (sincronizadas com os menus)
  const funcionalidades = ref(
    _menusCache.length > 0 ? _menusCache.map((m) => m.chave) : [],
  );

  // Computed
  const estaAutenticado = computed(() => !!cnpjLogado.value);
  const tokenExpirou = computed(() => {
    if (!tokenExpiracaoEm.value) return false;
    return new Date() > new Date(tokenExpiracaoEm.value);
  });

  // Retorna true se a empresa tem acesso à funcionalidade.
  // Lista vazia significa plano não carregado → libera tudo (backward compat).
  function temFuncionalidade(chave) {
    if (!chave) return true;
    if (!funcionalidades.value.length) return true;
    return funcionalidades.value.includes(chave);
  }

  // Actions
  function autenticar(cnpj, empresa) {
    cnpjLogado.value = cnpj;
    empresaLogada.value = empresa;
    tokenExpiracaoEm.value = new Date(Date.now() + 3600000); // 1 hora
    localStorage.setItem("cnpj_logado", cnpj);
  }

  function desautenticar() {
    cnpjLogado.value = "";
    empresaLogada.value = {};
    tokenExpiracaoEm.value = null;
    funcionalidades.value = [];
    menus.value = [];
    localStorage.removeItem("token");
    localStorage.removeItem("cnpj_logado");
    sessionStorage.removeItem("menus_cache");
  }

  function logout() {
    cnpjLogado.value = "";
    empresaLogada.value = {};
    funcionalidades.value = [];
    menus.value = [];
    localStorage.removeItem("token");
    localStorage.removeItem("cnpj_logado");
    sessionStorage.removeItem("menus_cache");
  }

  // Compatibilidade com código legado
  function definirCnpjLogado(cnpj) {
    cnpjLogado.value = cnpj;
  }

  function definirObjetoEmpresaLogada(empresa) {
    empresaLogada.value = empresa;
  }

  function atualizarObjParametro(param) {
    parametro.value = param;
  }

  function retornoObjParametro() {
    return parametro.value;
  }

  function definirFuncionalidades(lista) {
    if (Array.isArray(lista)) {
      funcionalidades.value = lista;
    } else if (typeof lista === "string" && lista.trim()) {
      funcionalidades.value = lista
        .split(",")
        .map((f) => f.trim())
        .filter(Boolean);
    } else {
      funcionalidades.value = [];
    }
  }

  function definirMenus(lista) {
    menus.value = Array.isArray(lista) ? lista : [];
    // Mantém funcionalidades em sincronia com as chaves dos menus
    if (menus.value.length > 0) {
      const chavesMenu = menus.value.map((m) => m.chave);
      // Também inclui features não-menu do plano (ex: agendamento_manual, animais, notificacoes).
      // A API /planos/menu-acessos só retorna is_menu=1; features internas ficam apenas no CSV.
      const csvEmpresa = empresaLogada.value?.funcionalidades ?? "";
      const chavesExtra = csvEmpresa
        .split(",")
        .map((f) => f.trim())
        .filter((f) => f && !chavesMenu.includes(f));
      funcionalidades.value = [...chavesMenu, ...chavesExtra];
      // Marca que esta sessão JS já recebeu menus frescos da API
      _menusCarregadosDaApi = true;
      // Persiste no sessionStorage para sobreviver a reloads (pico de energia, F5)
      try {
        sessionStorage.setItem("menus_cache", JSON.stringify(menus.value));
      } catch {
        // sessionStorage indisponível (modo privado restrito) — ignora
      }
    }
  }

  // Fallback estático: constrói o menu com base no CSV de funcionalidades da empresa.
  // Acionado quando a API /planos/menu-acessos falha (rede, timeout, pico de energia).
  // Garante que o menu NUNCA apareça completamente em branco para um usuário logado.
  function construirMenusDeFallback() {
    if (menus.value.length > 0) return false; // já tem menus — não sobrescreve
    const csv = empresaLogada.value?.funcionalidades ?? "";
    const chavesDaEmpresa = csv
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
    // Prioriza CSV da empresa; se não disponível, usa funcionalidades já carregadas
    const fonteChaves =
      chavesDaEmpresa.length > 0 ? chavesDaEmpresa : funcionalidades.value;
    const itens = fonteChaves
      .filter((c) => CATALOGO_ESTATICO[c])
      .map((c) => ({ chave: c, ...CATALOGO_ESTATICO[c] }));
    if (itens.length > 0) {
      definirMenus(itens);
      return true;
    }
    return false;
  }

  return {
    empresaLogada,
    cnpjLogado,
    parametro,
    funcionalidades,
    menus,
    estaAutenticado,
    tokenExpirou,
    temFuncionalidade,
    autenticar,
    desautenticar,
    logout,
    definirCnpjLogado,
    definirObjetoEmpresaLogada,
    atualizarObjParametro,
    retornoObjParametro,
    definirFuncionalidades,
    get menusCarregadosDaApi() {
      return _menusCarregadosDaApi;
    },
    definirMenus,
    construirMenusDeFallback,
  };
});
